import { useQueryClient } from "@tanstack/react-query";
import { useMesfRealtime } from "@dexteel/mesf-core";

/**
 * Maps stored procedure names to TanStack Query key prefixes for automatic invalidation.
 * When the backend executes one of these SPs, it sends a SignalR message
 * that triggers cache invalidation for all connected clients.
 *
 * NOTE: Charges and Charge Layers use polling (refetchInterval) for real-time updates,
 * so they are intentionally NOT listed here. Adding them would cause near-continuous
 * refetches on a busy site.
 */
const SP_TO_QUERY_KEYS: Record<string, readonly string[][]> = {
  "SCRAP.GetRecipes": [["recipes"]],
  "SCRAP.GetRecipeLayers": [["recipe-layers"]],
  "SCRAP.GetChargeLayerAudit": [["charge-layer-audit"]],
  "SCRAP.GetCommodities": [["commodities"]],
  "SCRAP.GetCommodityTypes": [["commodity-types"]],
  "SCRAP.GetCommoditySubstitutes": [["commodity-substitutes"]],
  "SCRAP.GetAvailableCommoditiesForSwitch": [["commodity-switch"]],
  "STEEL.GetBuckets": [["buckets"]],
  "STEEL.GetSYOperators": [["operators"]],
};

/**
 * Hook that listens to SignalR messages and invalidates TanStack Query cache.
 * This enables real-time updates across multiple users without polling.
 *
 * Usage: Call this hook once at the app/page level where you want real-time updates.
 */
export const useRealtimeInvalidation = () => {
  const queryClient = useQueryClient();

  useMesfRealtime({
    onReceiveMessage: (_author: string, message: string) => {
      const queryKeys = SP_TO_QUERY_KEYS[message];
      if (queryKeys) {
        queryKeys.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
    },
  });
};
